import { findArea, findLineDistance } from '@/lib/math'
import { MARKUP_CREATE_ORIGINS } from '@/lib/enums-marketing'

import marketingEventService from '@/services/marketing-event-service'
import { viewerDimension } from '@/services/map-service'
import { eventsStore } from '@/services/route-service'
import { DEFAULT_MARKUP_NAME_REGEX } from '@/services/markup-service/constants'

import type { GeolibInputCoordinates } from 'geolib/es/types'
import type { EventProps } from '@/lib/marketing'
import type { Line, Route, Shape, Track, Waypoint } from '@onxmaps/markups-web'

/**
 * Centralized markup event publishing service for markup events.
 */

// ===============================
//         SHAPE EVENTS
// ===============================

/**
 * Helper function to get shape event properties
 */
function getShapeEventProperties(shape: Shape) {
  const totalArea = findArea(
    shape.coordinates[0] as GeolibInputCoordinates[],
    'imperial'
  ).area

  const totalPerimeter = findLineDistance(
    shape.coordinates[0] as GeolibInputCoordinates[]
  )

  return {
    custom_name: !DEFAULT_MARKUP_NAME_REGEX.test(shape.name),
    free_hand: shape.isFreeHand ?? false,
    markup_uuid: shape.uuid,
    notes_populated: !!(shape.notes ?? '').trim(),
    num_points: shape.coordinates[0]?.length,
    total_area: totalArea,
    total_perimeter: totalPerimeter,
  }
}

/**
 * Publish marketing event for saving a new shape
 */
export function publishShapeCreated(shape: Shape, eventProps?: EventProps) {
  marketingEventService.publish('area_create_saved', {
    ...getShapeEventProperties(shape),
    ...eventProps,
  })
}

/**
 * Publish marketing event for updating an existing shape
 */
export function publishShapeUpdated(shape: Shape, eventProps?: EventProps) {
  marketingEventService.publish('area_edit_saved', {
    ...getShapeEventProperties(shape),
    ...eventProps,
  })
}

/**
 * Publish marketing event for deleting a shape
 */
export function publishShapeDeleted(shape: Shape, eventProps?: EventProps) {
  marketingEventService.publish('area_deleted', {
    ...getShapeEventProperties(shape),
    ...eventProps,
  })
}

/**
 * Publish marketing event for discarding shape create/edit
 */
export function publishShapeDiscarded(
  shape: Shape,
  isCreate: boolean,
  eventProps?: EventProps
) {
  const name = isCreate ? `area_create_discarded` : `area_edit_discarded`

  marketingEventService.publish(name, {
    markup_uuid: shape.uuid,
    free_hand: shape.isFreeHand ?? false,
    ...eventProps,
  })
}

// ===============================
//         WAYPOINT EVENTS
// ===============================

/**
 * Helper function to get waypoint event properties
 */
function getWaypointEventProperties(waypoint: Waypoint) {
  const waypointIcon = waypoint.properties?.icon
  return {
    custom_name: !DEFAULT_MARKUP_NAME_REGEX.test(waypoint.name),
    markup_uuid: waypoint.uuid,
    notes_populated: !!(waypoint.notes ?? '').trim(),
    photo_count: waypoint.photos?.length ?? 0,
    waypoint_icon: waypointIcon,
  }
}

/**
 * Publish marketing event for saving a new waypoint
 */
export function publishWaypointCreated(
  waypoint: Waypoint,
  eventProps?: EventProps
) {
  marketingEventService.publish('waypoint_create_saved', {
    ...getWaypointEventProperties(waypoint),
    photo_count:
      (waypoint.photos?.length ?? 0) + (waypoint.pendingPhotoCount ?? 0),
    ...eventProps,
  })
}

/**
 * Publish marketing event for updating an existing waypoint
 */
export function publishWaypointUpdated(
  waypoint: Waypoint,
  eventProps?: EventProps
) {
  marketingEventService.publish('waypoint_edit_saved', {
    ...getWaypointEventProperties(waypoint),
    ...eventProps,
  })
}

/**
 * Publish marketing event for deleting a waypoint
 */
export function publishWaypointDeleted(
  waypoint: Waypoint,
  eventProps?: EventProps
) {
  marketingEventService.publish('waypoint_deleted', {
    ...getWaypointEventProperties(waypoint),
    ...eventProps,
  })
}

/**
 * Publish marketing event for discarding waypoint create/edit
 */
export function publishWaypointDiscarded(
  waypoint: Waypoint,
  isCreate: boolean,
  eventProps?: EventProps
) {
  const name = isCreate
    ? 'waypoint_create_discarded'
    : 'waypoint_edit_discarded'

  marketingEventService.publish(name, {
    markup_uuid: waypoint.uuid,
    ...eventProps,
  })
}

// ===============================
//         LINE EVENTS
// ===============================

/**
 * Helper function to get line event properties
 */
function getLineEventProperties(line: Line) {
  const totalDistance = findLineDistance(
    line.coordinates as GeolibInputCoordinates[]
  )

  return {
    custom_name: !DEFAULT_MARKUP_NAME_REGEX.test(line.name),
    free_hand: line.isFreeHand ?? false,
    markup_uuid: line.uuid,
    notes_populated: !!(line.notes ?? '').trim(),
    num_points: line.coordinates.length,
    total_distance: totalDistance,
  }
}

/**
 * Publish marketing event for saving a new line
 */
export function publishLineCreated(line: Line, eventProps?: EventProps) {
  marketingEventService.publish('line_create_saved', {
    ...getLineEventProperties(line),
    ...eventProps,
  })
}

/**
 * Publish marketing event for updating an existing line
 */
export function publishLineUpdated(line: Line, eventProps?: EventProps) {
  marketingEventService.publish('line_edit_saved', {
    ...getLineEventProperties(line),
    ...eventProps,
  })
}

/**
 * Publish marketing event for deleting a line
 */
export function publishLineDeleted(line: Line, eventProps?: EventProps) {
  marketingEventService.publish('line_deleted', {
    ...getLineEventProperties(line),
    ...eventProps,
  })
}

/**
 * Publish marketing event for discarding line
 */
export function publishLineDiscarded(
  line: Line,
  isCreate: boolean,
  eventProps?: EventProps
) {
  const name = isCreate ? 'line_create_discarded' : 'line_edit_discarded'

  marketingEventService.publish(name, {
    markup_uuid: line.uuid,
    free_hand: line.isFreeHand ?? false,
    ...eventProps,
  })
}

// ===============================
//         TRACK EVENTS
// ===============================

/**
 * Helper function to get track event properties
 */
function getTrackEventProperties(track: Track) {
  const totalDistance = findLineDistance(
    track.coordinates as GeolibInputCoordinates[]
  )

  return {
    custom_name: !DEFAULT_MARKUP_NAME_REGEX.test(track.name),
    markup_uuid: track.uuid,
    notes_populated: !!(track.notes ?? '').trim(),
    num_points: track.coordinates.length,
    total_distance: totalDistance,
  }
}

/**
 * Publish marketing event for saving a new track
 */
export function publishTrackCreated(track: Track, eventProps?: EventProps) {
  marketingEventService.publish('track_create_saved', {
    ...getTrackEventProperties(track),
    ...eventProps,
  })
}

/**
 * Publish marketing event for updating an existing track
 */
export function publishTrackUpdated(track: Track, eventProps?: EventProps) {
  marketingEventService.publish('track_edit_saved', {
    ...getTrackEventProperties(track),
    ...eventProps,
  })
}

/**
 * Publish marketing event for deleting a track
 */
export function publishTrackDeleted(track: Track, eventProps?: EventProps) {
  marketingEventService.publish('track_deleted', {
    ...getTrackEventProperties(track),
    ...eventProps,
  })
}

/**
 * Publish marketing event for discarding track
 */
export function publishTrackDiscarded(
  track: Track,
  isCreate: boolean,
  eventProps?: EventProps
) {
  const name = isCreate ? 'track_create_discarded' : 'track_edit_discarded'

  marketingEventService.publish(name, {
    markup_uuid: track.uuid,
    ...eventProps,
  })
}

// ===============================
//         ROUTE EVENTS
// ===============================

/**
 * Helper function to get common route event properties
 */
function getCommonRouteEventProperties(route: Route) {
  return {
    activity_type: route.rawData?.route?.legs?.[0]?.activity,
    card_size_at_exit:
      eventsStore.collapsedCount % 2 === 0 ? 'collapsed' : 'expanded',
    clicked_clear: eventsStore.clearClickedCount,
    clicked_help: eventsStore.helpClickedCount,
    clicked_PointDraw: eventsStore.pointDrawClickedCount,
    clicked_SnapTo: eventsStore.snapToClickedCount,
    clicked_undo: eventsStore.undoClickedCount,
    collapsed_card: eventsStore.collapsedCount,
    color: route.rawData?.color,
    errors: eventsStore.errors,
    map_viewer_dimension: viewerDimension(),
    markup_uuid: route.uuid,
    num_points: route.rawData?.route?.legs?.[0]?.waypoints?.length,
    style: route.rawData?.style,
    total_distance: parseInt(`${route.rawData?.route?.distance}`),
    weight: route.rawData?.weight,
  }
}

/**
 * Publish marketing event for starting a route
 */
export function publishRouteStarted(props: EventProps) {
  marketingEventService.publish('route_build_initiated', {
    map_viewer_dimension: viewerDimension(),
    ...props,
  })
}

/**
 * Publish marketing event for editing a route
 */
export function publishRouteEdited(route: Route, props: EventProps = {}) {
  marketingEventService.publish('route_edit_initiated', {
    markup_uuid: route.uuid,
    ...props,
  })
}

/**
 * Publish marketing event for saving a route
 */
export function publishRouteSaved(newMarkup: Route, props: EventProps) {
  const eventProps = {
    ...getCommonRouteEventProperties(newMarkup),
    route_build_method: '',
    ...props,
  }

  if (
    props &&
    'origin' in props &&
    [
      MARKUP_CREATE_ORIGINS.ROUTE_QUERY_CARD,
      MARKUP_CREATE_ORIGINS.ROUTE_RICH_CONTENT_CARD,
      MARKUP_CREATE_ORIGINS.ROUTE_MARKUP_INFO_CARD,
    ].includes(
      props.origin as
        | typeof MARKUP_CREATE_ORIGINS.ROUTE_QUERY_CARD
        | typeof MARKUP_CREATE_ORIGINS.ROUTE_RICH_CONTENT_CARD
        | typeof MARKUP_CREATE_ORIGINS.ROUTE_MARKUP_INFO_CARD
    )
  ) {
    eventProps.route_build_method = 'route to'
  }

  marketingEventService.publish('route_build_saved', eventProps)
}

/**
 * Publish marketing event for updating an existing route
 */
export function publishRouteUpdated(route: Route, eventProps: EventProps) {
  marketingEventService.publish('route_edit_saved', {
    ...getCommonRouteEventProperties(route),
    ...eventProps,
  })
}

/**
 * Publish marketing event for deleting a route
 */
export function publishRouteDeleted(route: Route, props: EventProps) {
  const { activity_type, markup_uuid, total_distance } =
    getCommonRouteEventProperties(route)

  marketingEventService.publish('route_deleted', {
    activity_type,
    markup_uuid,
    total_distance,
    ...props,
  })
}

/**
 * Publish marketing event for discarding route edit
 */
export function publishRouteEditDiscarded(route: Route, props: EventProps) {
  marketingEventService.publish('route_edit_discarded', {
    ...getCommonRouteEventProperties(route),
    ...props,
  })
}

/**
 * Publish marketing event for discarding new route build
 * Note: Uses geo_json.properties for color/style/weight (not rawData)
 */
export function publishRouteBuildDiscarded(route: Route, props: EventProps) {
  const geoProps =
    route.toGeoJSON?.().properties ?? (route as Route).geoJson?.properties
  marketingEventService.publish('route_build_discarded', {
    ...getCommonRouteEventProperties(route),
    color: geoProps?.color,
    style: geoProps?.style,
    weight: geoProps?.weight,
    ...props,
  })
}
